import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { logApiHeaders } from '../utils/env';
import { 
  LoginResponse, 
  UserResource,
  CompanyResource,
  CompanyBranchResource,
  CompanyTableResource,
  CompanySettingResource,
  CompanyReceiptSettingResource,
  CategoryResource,
  DiscountResource,
  SupplierResource,
  PurchaseResource,
  StockTakingResource,
  SubscriptionResource,
  ExpenseResource,
  ProductOptionTemplateResource,
  KloaProgramCouponResource,
  CompanyUserResource,
  ProductResource,
  SaleResource,
  SaleDetailResource,
  SaleDetailResponse,
  CompanySubscriptionResource,
  AvailableSubscriptionResource,
  AssignSubscriptionRequest,
  AdminSubscriptionInvoiceResource,
  CreateSubscriptionRequest,
  MarketingProgramResource,
  CreateMarketingProgramRequest,
  KloaMarketingProgramCouponResource,
  PaginatedResponse,
  ApiResponse,
  DashboardStatsResponse,
  SubscriptionInvoiceGraphResponse,
  MRRGraphResponse,
  SalesGraphResponse,
  UsersGraphResponse,
  StoreCompanyBranchRequest,
  UpdateCompanyBranchRequest,
  StoreCompanyTableRequest,
  CategoryRequest,
  StockTakingRequest,
  CompanySettingRequest,
  CompanyReceiptSettingRequest,
  CreateSubscriptionInvoiceRequest,
  StoreProductOptionTemplateRequest,
  AffiliateHistoriesResponse,
  CommissionWithdrawsResponse,
  AffiliateStatsResponse,
  CompanyAffiliateResource,
  RegisterReferrerPayload,
  ReferralAuditLogEntry,
  AffiliateCommissionRate,
  SetAffiliateCommissionRatePayload,
  CompanyAffiliateDetailResponse,
  CompanyAffiliateStatsResponse,
  CompanyAffiliateHistoriesByCompanyResponse,
  CompanyCommissionHistoriesByCompanyResponse,
  MonthlyReportResponse,
  AdminStoreItemResource,
  CreateStoreItemRequest,
  AdminStoreInvoiceResource,
  MarkStoreInvoicePaidRequest,
  MarketingChannelSummaryResponse,
  CompanyGeoSummaryResponse,
  SubscriptionVoucherResource,
  GenerateVoucherRequest,
  AdminUser,
  Lead,
  CompanyFollowUp,
  FollowUpCompany,
  SalesDashboard,
  MarketingDashboard,
  ReviewFeedbackResource,
  SubscriptionBundleResource,
  SubscriptionBundleRequest,
  ManualPurchaseResource,
  ManualPurchasePreview,
  ManualPurchaseExtraItem,
  ManualPurchaseStatus,
  QrisAccountStatus,
  QrisAccountType,
  QrisAccountListItem,
  QrisAccountDetail,
  AdminMenuImportRow,
  AdminMenuImportDetail,
  AdminMenuImportSummary,
  MenuImportSettings,
  AdminMenuImportFilters,
} from '../types';

class ApiService {
  private api: AxiosInstance;
  private baseURL: string;

  constructor() {
    // Validate required environment variables
    if (!process.env.REACT_APP_API_URL) {
      console.warn('REACT_APP_API_URL is not set, using default: http://localhost:8000/api');
    }
    if (!process.env.REACT_APP_ADMIN_X_API_KEY) {
      console.error('REACT_APP_ADMIN_X_API_KEY is required but not set!');
    }
    if (!process.env.REACT_APP_X_API_KEY) {
      console.error('REACT_APP_X_API_KEY is required but not set!');
    }
    if (!process.env.REACT_APP_API_LEVEL) {
      console.warn('REACT_APP_API_LEVEL is not set, using default: 1');
    }

    this.baseURL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
    this.api = axios.create({
      baseURL: this.baseURL,
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest', // Laravel expects this for AJAX requests
      },
      // Note: withCredentials removed since we're not using CSRF tokens
    });

    // Add request interceptor to include required headers for all requests
    this.api.interceptors.request.use(
      async (config) => {
        // Always include admin API key for ALL requests (including login)
        if (process.env.REACT_APP_ADMIN_X_API_KEY) {
          config.headers['ADMIN-X-API-KEY'] = process.env.REACT_APP_ADMIN_X_API_KEY;
        } else {
          console.error('ADMIN-X-API-KEY environment variable is not set!');
        }

        // Always include general API key for ALL requests (including login)
        if (process.env.REACT_APP_X_API_KEY) {
          config.headers['X-API-KEY'] = process.env.REACT_APP_X_API_KEY;
        } else {
          console.error('X-API-KEY environment variable is not set!');
        }

        // Always include API level for ALL requests (including login)
        config.headers['api-level'] = process.env.REACT_APP_API_LEVEL || '1';
        
        // Note: CSRF token handling removed - your Laravel backend might not need it for API endpoints
        // If you need CSRF tokens, uncomment the code below:
        /*
        if (config.method !== 'get' && config.url !== '/admin/login') {
          try {
            const csrfToken = await csrfService.getCsrfToken();
            config.headers['X-CSRF-TOKEN'] = csrfToken;
          } catch (error) {
            console.warn('Failed to get CSRF token:', error);
          }
        }
        */

        // Add Bearer token if available
        const token = localStorage.getItem('authToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Log headers for debugging (only in development)
        if (process.env.NODE_ENV === 'development') {
          logApiHeaders(config);
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add response interceptor to handle auth errors
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Unauthorized - clear tokens and redirect to login
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
          window.location.href = '/login';
        } else if (error.response?.status === 403) {
          // Forbidden - might be API key or level issue
          console.error('Access forbidden. Check API key and level permissions.');
        }
        return Promise.reject(error);
      }
    );
  }

  // Auth endpoints
  async login(email: string, password: string): Promise<LoginResponse> {
    // Login endpoint doesn't need admin headers - they're added by interceptor
    const response: AxiosResponse<LoginResponse> = await this.api.post('/admin/login', {
      email,
      password,
    });
    return response.data;
  }

          // Company endpoints
        async getCompanies(page: number = 1, perPage: number = 10, search?: string, isNeedFollowUp?: boolean, orderBy?: string, extra?: { marketingChannelId?: string; startDate?: string; endDate?: string; provinsi?: string; kabupaten?: string; isActivelyUsing?: boolean }): Promise<PaginatedResponse<CompanyResource>> {
          const params: any = { page, perPage, search };
          if (isNeedFollowUp !== undefined) {
            params.isNeedFollowUp = isNeedFollowUp ? '1' : '0';
          }
          if (orderBy !== undefined) {
            params.orderBy = orderBy;
          }
          if (extra?.marketingChannelId) {
            params.marketingChannelId = extra.marketingChannelId;
          }
          if (extra?.startDate) {
            params.startDate = extra.startDate;
          }
          if (extra?.endDate) {
            params.endDate = extra.endDate;
          }
          if (extra?.provinsi) {
            params.provinsi = extra.provinsi;
          }
          if (extra?.kabupaten) {
            params.kabupaten = extra.kabupaten;
          }
          if (extra?.isActivelyUsing) {
            params.isActivelyUsing = '1';
          }
          const response: AxiosResponse<PaginatedResponse<CompanyResource>> = await this.api.get('/admin/companies', {
            params
          });
          return response.data;
        }

        async getCompanyUsers(companyId: string, page: number = 1, perPage: number = 10, search?: string): Promise<PaginatedResponse<CompanyUserResource>> {
          const response: AxiosResponse<PaginatedResponse<CompanyUserResource>> = await this.api.get(`/admin/companies/${companyId}/users`, {
            params: { page, perPage, search }
          });
          return response.data;
        }

        async getCompanyBranches(companyId: string, page: number = 1, perPage: number = 10, search?: string): Promise<PaginatedResponse<CompanyBranchResource>> {
          const response: AxiosResponse<{ data: CompanyBranchResource[] }> = await this.api.get(`/companies/${companyId}/branches`, {
            params: { page, perPage, search }
          });
          // Transform the response to match PaginatedResponse structure
          return {
            data: response.data.data,
            links: {
              first: '',
              last: '',
              prev: null,
              next: null
            },
            meta: {
              current_page: 1,
              from: 1,
              last_page: 1,
              links: [],
              path: '',
              per_page: response.data.data.length,
              to: response.data.data.length,
              total: response.data.data.length
            }
          };
        }

        async getCompanyProducts(
          companyId: string, 
          page: number = 1, 
          perPage: number = 10, 
          search?: string,
          filters?: {
            isBundle?: boolean | number;
            isActive?: boolean | number;
            isFavorite?: boolean | number;
          }
        ): Promise<PaginatedResponse<ProductResource>> {
          // Convert boolean filters to proper format for API
          const apiFilters: any = {};
          if (filters) {
            Object.entries(filters).forEach(([key, value]) => {
              if (value !== undefined && value !== null) {
                // Convert boolean to 1/0 format for API compatibility
                apiFilters[key] = typeof value === 'boolean' ? (value ? 1 : 0) : value;
              }
            });
          }

          const response: AxiosResponse<PaginatedResponse<ProductResource>> = await this.api.get(`/admin/companies/${companyId}/products`, {
            params: { page, perPage, search, ...apiFilters }
          });
          return response.data;
        }

        async getCompanySales(
          companyId: string,
          page: number = 1,
          perPage: number = 10,
          search?: string,
          filters?: {
            status?: 'unpaid' | 'paid' | 'void';
            paymentMethod?: 'cash' | 'debit' | 'online' | 'qris';
            startDate?: string;
            endDate?: string;
            orderBy?: string;
          }
        ): Promise<PaginatedResponse<SaleResource>> {
          const response: AxiosResponse<PaginatedResponse<SaleResource>> = await this.api.get(`/admin/companies/${companyId}/sales`, {
            params: { page, perPage, search, ...filters }
          });
          return response.data;
        }

        async getSaleDetail(saleId: string): Promise<SaleDetailResource> {
          const response: AxiosResponse<SaleDetailResponse> = await this.api.get(`/admin/sales/${saleId}`);
          return response.data.data;
        }

        async getCompanySubscriptions(
          companyId: string
        ): Promise<CompanySubscriptionResource[]> {
          const response: AxiosResponse<{ data: CompanySubscriptionResource[] }> = await this.api.get(`/admin/companies/${companyId}/company-subscriptions`);
          return response.data.data;
        }

        async getAvailableSubscriptions(): Promise<AvailableSubscriptionResource[]> {
          const response: AxiosResponse<{ data: AvailableSubscriptionResource[] }> = await this.api.get('/admin/subscriptions');
          return response.data.data;
        }

        async assignSubscription(
          companyId: string,
          subscriptionId: string,
          data: AssignSubscriptionRequest
        ): Promise<any> {
          const response = await this.api.post(`/admin/companies/${companyId}/assign-subscription/${subscriptionId}`, data);
          return response.data;
        }

        async getSubscriptionInvoices(
          page: number = 1,
          perPage: number = 10,
          search?: string,
          filters?: {
            status?: 'paid' | 'unpaid' | 'expired';
            companyId?: string;
            subscriptionId?: string;
          }
        ): Promise<PaginatedResponse<AdminSubscriptionInvoiceResource>> {
          const response: AxiosResponse<PaginatedResponse<AdminSubscriptionInvoiceResource>> = await this.api.get('/admin/subscription-invoices', {
            params: { page, perPage, search, ...filters }
          });
          return response.data;
        }

        // Subscription Management APIs
        async getSubscriptions(
          page: number = 1,
          perPage: number = 10,
          search?: string,
          filters?: {
            isShow?: boolean;
          }
        ): Promise<PaginatedResponse<AvailableSubscriptionResource>> {
          const response: AxiosResponse<PaginatedResponse<AvailableSubscriptionResource>> = await this.api.get('/admin/subscriptions', {
            params: { page, perPage, search, ...filters }
          });
          return response.data;
        }

        async getAdminSubscription(subscriptionId: string): Promise<AvailableSubscriptionResource> {
          const response: AxiosResponse<AvailableSubscriptionResource> = await this.api.get(`/admin/subscriptions/${subscriptionId}`);
          return response.data;
        }

        async createSubscription(data: CreateSubscriptionRequest): Promise<AvailableSubscriptionResource> {
          const response: AxiosResponse<AvailableSubscriptionResource> = await this.api.post('/admin/subscriptions', data);
          return response.data;
        }

        async updateSubscription(subscriptionId: string, data: Partial<CreateSubscriptionRequest>): Promise<AvailableSubscriptionResource> {
          const response: AxiosResponse<AvailableSubscriptionResource> = await this.api.put(`/admin/subscriptions/${subscriptionId}`, data);
          return response.data;
        }

        async deleteSubscription(subscriptionId: string): Promise<any> {
          const response = await this.api.delete(`/admin/subscriptions/${subscriptionId}`);
          return response.data;
        }

  async getCompanyCategories(companyId: string): Promise<CategoryResource[]> {
    const response: AxiosResponse<CategoryResource[]> = await this.api.get(`/companies/${companyId}/categories`);
    return response.data;
  }



  async createCompanyBranch(companyId: string, data: StoreCompanyBranchRequest): Promise<CompanyBranchResource> {
    const response: AxiosResponse<CompanyBranchResource> = await this.api.post(`/companies/${companyId}/branches`, data);
    return response.data;
  }

  async updateCompanyBranch(branchId: string, data: UpdateCompanyBranchRequest): Promise<CompanyBranchResource> {
    const response: AxiosResponse<CompanyBranchResource> = await this.api.put(`/branches/${branchId}`, data);
    return response.data;
  }

  async deleteCompanyBranch(branchId: string): Promise<ApiResponse<null>> {
    const response: AxiosResponse<ApiResponse<null>> = await this.api.delete(`/branches/${branchId}`);
    return response.data;
  }

  async getBranchTables(branchId: string): Promise<CompanyTableResource[]> {
    const response: AxiosResponse<CompanyTableResource[]> = await this.api.get(`/branches/${branchId}/tables`);
    return response.data;
  }

  async createBranchTable(branchId: string, data: StoreCompanyTableRequest): Promise<CompanyTableResource> {
    const response: AxiosResponse<CompanyTableResource> = await this.api.post(`/branches/${branchId}/tables`, data);
    return response.data;
  }

  // Category endpoints
  async getCategories(page: number, perPage: number = 10): Promise<PaginatedResponse<CategoryResource>> {
    const response: AxiosResponse<PaginatedResponse<CategoryResource>> = await this.api.get('/categories', {
      params: { page, perPage }
    });
    return response.data;
  }

  async createCategory(data: CategoryRequest): Promise<CategoryResource> {
    const response: AxiosResponse<CategoryResource> = await this.api.post('/categories', data);
    return response.data;
  }

  async updateCategory(categoryId: string, data: CategoryRequest): Promise<CategoryResource> {
    const response: AxiosResponse<CategoryResource> = await this.api.put(`/categories/${categoryId}`, data);
    return response.data;
  }

  async deleteCategory(categoryId: string): Promise<ApiResponse<null>> {
    const response: AxiosResponse<ApiResponse<null>> = await this.api.delete(`/categories/${categoryId}`);
    return response.data;
  }

  // Discount endpoints
  async getDiscounts(page: number, perPage: number = 10): Promise<PaginatedResponse<DiscountResource>> {
    const response: AxiosResponse<PaginatedResponse<DiscountResource>> = await this.api.get('/discounts', {
      params: { page, perPage }
    });
    return response.data;
  }

  async calculateDiscountValue(discountId: string, amount: number): Promise<{ discountAmount: number; finalAmount: number }> {
    const response: AxiosResponse<{ discountAmount: number; finalAmount: number }> = await this.api.post(`/discounts/${discountId}/count-value`, { amount });
    return response.data;
  }

  // Supplier endpoints
  async getSuppliers(page: number, perPage: number = 10): Promise<PaginatedResponse<SupplierResource>> {
    const response: AxiosResponse<PaginatedResponse<SupplierResource>> = await this.api.get('/suppliers', {
      params: { page, perPage }
    });
    return response.data;
  }

  // Purchase endpoints
  async getPurchases(page: number, perPage: number = 10, filters?: {
    supplierId?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<PaginatedResponse<PurchaseResource>> {
    const response: AxiosResponse<PaginatedResponse<PurchaseResource>> = await this.api.get('/purchases', {
      params: { page, perPage, ...filters }
    });
    return response.data;
  }

  async getPurchaseDetail(purchaseId: string): Promise<PurchaseResource> {
    const response: AxiosResponse<PurchaseResource> = await this.api.get(`/purchases/${purchaseId}`);
    return response.data;
  }

  // Stock Taking endpoints
  async getStockTakings(page: number, perPage: number = 10, filters?: {
    type?: string;
    companyBranchId?: string;
  }): Promise<PaginatedResponse<StockTakingResource>> {
    const response: AxiosResponse<PaginatedResponse<StockTakingResource>> = await this.api.get('/stock-takings', {
      params: { page, perPage, ...filters }
    });
    return response.data;
  }

  async createStockTaking(data: StockTakingRequest): Promise<StockTakingResource> {
    const response: AxiosResponse<StockTakingResource> = await this.api.post('/stock-takings', data);
    return response.data;
  }

  // Subscription endpoints
  async getSubscription(subscriptionId: string): Promise<SubscriptionResource> {
    const response: AxiosResponse<SubscriptionResource> = await this.api.get(`/subscriptions/${subscriptionId}`);
    return response.data;
  }

  async createSubscriptionInvoice(subscriptionId: string, data: CreateSubscriptionInvoiceRequest): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post(`/subscriptions/${subscriptionId}/subscription-invoices`, data);
    return response.data;
  }

  // Company Settings endpoints
  async updateCompanySettings(data: CompanySettingRequest): Promise<CompanySettingResource> {
    const response: AxiosResponse<CompanySettingResource> = await this.api.post('/company-settings', data);
    return response.data;
  }

  async getMyCompanySettings(): Promise<CompanySettingResource> {
    const response: AxiosResponse<CompanySettingResource> = await this.api.get('/company-settings/me');
    return response.data;
  }

  async updateCompanyReceiptSettings(data: CompanyReceiptSettingRequest): Promise<CompanyReceiptSettingResource> {
    const response: AxiosResponse<CompanyReceiptSettingResource> = await this.api.post('/company-receipt-settings', data);
    return response.data;
  }

  async getMyCompanyReceiptSettings(): Promise<CompanyReceiptSettingResource> {
    const response: AxiosResponse<CompanyReceiptSettingResource> = await this.api.get('/company-receipt-settings/me');
    return response.data;
  }

  // Expense endpoints
  async getExpenses(page: number, perPage: number = 10, filters?: {
    companyBranchId?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<PaginatedResponse<ExpenseResource>> {
    const response: AxiosResponse<PaginatedResponse<ExpenseResource>> = await this.api.get('/expenses', {
      params: { page, perPage, ...filters }
    });
    return response.data;
  }

  async createExpense(data: any): Promise<ExpenseResource> {
    const response: AxiosResponse<ExpenseResource> = await this.api.post('/expenses', data);
    return response.data;
  }

  async getExpenseDetail(expenseId: string): Promise<ExpenseResource> {
    const response: AxiosResponse<ExpenseResource> = await this.api.get(`/expenses/${expenseId}`);
    return response.data;
  }

  // Product Option Template endpoints
  async getProductOptionTemplates(page: number, perPage: number = 10): Promise<PaginatedResponse<ProductOptionTemplateResource>> {
    const response: AxiosResponse<PaginatedResponse<ProductOptionTemplateResource>> = await this.api.get('/product-option-templates', {
      params: { page, perPage }
    });
    return response.data;
  }

  async createProductOptionTemplate(data: StoreProductOptionTemplateRequest): Promise<ProductOptionTemplateResource> {
    const response: AxiosResponse<ProductOptionTemplateResource> = await this.api.post('/product-option-templates', data);
    return response.data;
  }

  // Cache endpoints
  async clearDiscountCache(): Promise<ApiResponse<{ company_id: string }>> {
    const response: AxiosResponse<ApiResponse<{ company_id: string }>> = await this.api.delete('/cache/discounts/clear');
    return response.data;
  }

  async clearBranchCache(): Promise<ApiResponse<{ company_id: string }>> {
    const response: AxiosResponse<ApiResponse<{ company_id: string }>> = await this.api.delete('/cache/branches/clear');
    return response.data;
  }

  // Marketing endpoints
  async getMarketingChannelSummary(startDate?: string, endDate?: string, isActivelyUsing?: boolean): Promise<MarketingChannelSummaryResponse> {
    const params: any = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (isActivelyUsing) params.isActivelyUsing = '1';
    const response: AxiosResponse<{ data: MarketingChannelSummaryResponse }> = await this.api.get('/admin/marketing-channels/summary', { params });
    return response.data.data;
  }

  async getCompanyGeoSummary(isActivelyUsing?: boolean): Promise<CompanyGeoSummaryResponse> {
    const params: any = {};
    if (isActivelyUsing) params.isActivelyUsing = '1';
    const response: AxiosResponse<{ data: CompanyGeoSummaryResponse }> = await this.api.get('/admin/companies/geo-summary', { params });
    return response.data.data;
  }

  async getMyAvailableCoupons(): Promise<KloaProgramCouponResource[]> {
    const response: AxiosResponse<KloaProgramCouponResource[]> = await this.api.get('/kloa-marketing-programs/my-available-coupon');
    return response.data;
  }

  async claimCoupon(couponId: string): Promise<ApiResponse<KloaProgramCouponResource>> {
    const response: AxiosResponse<ApiResponse<KloaProgramCouponResource>> = await this.api.post('/kloa-marketing-programs/claim-coupon', { couponId });
    return response.data;
  }

  // Marketing Programs CRUD
  async getMarketingPrograms(
    page: number = 1,
    perPage: number = 10,
    search?: string,
    filters?: {
      type?: 'coupon' | 'no-coupon';
      isActive?: boolean;
      startDate?: string;
      endDate?: string;
    }
  ): Promise<PaginatedResponse<MarketingProgramResource>> {
    const params: any = { page, perPage, search };
    
    if (filters) {
      if (filters.type) params.type = filters.type;
      if (filters.isActive !== undefined) params.isActive = filters.isActive ? '1' : '0';
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
    }
    
    const response: AxiosResponse<PaginatedResponse<MarketingProgramResource>> = await this.api.get('/admin/kloa-marketing-programs', {
      params
    });
    return response.data;
  }

  async createMarketingProgram(data: CreateMarketingProgramRequest): Promise<MarketingProgramResource> {
    const formData = new FormData();
    
    // Add all fields to FormData
    formData.append('name', data.name);
    if (data.description) formData.append('description', data.description);
    formData.append('type', data.type);
    if (data.programCode) formData.append('programCode', data.programCode);
    if (data.couponPrefix) formData.append('couponPrefix', data.couponPrefix);
    formData.append('minimumMonth', data.minimumMonth.toString());
    formData.append('discountType', data.discountType);
    formData.append('discountValue', data.discountValue.toString());
    formData.append('isActive', data.isActive ? '1' : '0');
    if (data.image) formData.append('image', data.image);
    if (data.usageLimit) formData.append('usageLimit', data.usageLimit.toString());
    if (data.usageLimitPerUser) formData.append('usageLimitPerUser', data.usageLimitPerUser.toString());
    if (data.startsAt) formData.append('startsAt', data.startsAt);
    if (data.endsAt) formData.append('endsAt', data.endsAt);
    if (data.expiredDay) formData.append('expiredDay', data.expiredDay.toString());

    const response: AxiosResponse<MarketingProgramResource> = await this.api.post('/admin/kloa-marketing-programs', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async updateMarketingProgram(programId: string, data: Partial<CreateMarketingProgramRequest>): Promise<MarketingProgramResource> {
    const formData = new FormData();
    
    // Add _method = "PUT" for Laravel method spoofing
    formData.append('_method', 'PUT');
    
    // Add all fields to FormData
    if (data.name) formData.append('name', data.name);
    if (data.description !== undefined) formData.append('description', data.description || '');
    if (data.type) formData.append('type', data.type);
    if (data.programCode !== undefined) formData.append('programCode', data.programCode || '');
    if (data.couponPrefix !== undefined) formData.append('couponPrefix', data.couponPrefix || '');
    if (data.minimumMonth) formData.append('minimumMonth', data.minimumMonth.toString());
    if (data.discountType) formData.append('discountType', data.discountType);
    if (data.discountValue) formData.append('discountValue', data.discountValue.toString());
    if (data.isActive !== undefined) formData.append('isActive', data.isActive ? '1' : '0');
    if (data.image) formData.append('image', data.image);
    if (data.usageLimit !== undefined) formData.append('usageLimit', data.usageLimit?.toString() || '');
    if (data.usageLimitPerUser !== undefined) formData.append('usageLimitPerUser', data.usageLimitPerUser?.toString() || '');
    if (data.startsAt !== undefined) formData.append('startsAt', data.startsAt || '');
    if (data.endsAt !== undefined) formData.append('endsAt', data.endsAt || '');
    if (data.expiredDay !== undefined) formData.append('expiredDay', data.expiredDay?.toString() || '');

    const response: AxiosResponse<MarketingProgramResource> = await this.api.post(`/admin/kloa-marketing-programs/${programId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async deleteMarketingProgram(programId: string): Promise<any> {
    const response = await this.api.delete(`/admin/kloa-marketing-programs/${programId}`);
    return response.data;
  }

  async getMarketingProgramCoupons(programId: string): Promise<KloaMarketingProgramCouponResource[]> {
    const response: AxiosResponse<{ data: KloaMarketingProgramCouponResource[] }> = await this.api.get(`/admin/kloa-marketing-programs/${programId}/coupons`);
    return response.data.data;
  }

  async generateCoupon(programId: string, companyId: string): Promise<any> {
    const response = await this.api.post(`/admin/kloa-marketing-programs/${programId}/generate-coupon/${companyId}`);
    return response.data;
  }

  async generateEmptyCoupons(programId: string, generateQty: number): Promise<any> {
    const response = await this.api.post(`/admin/kloa-marketing-programs/${programId}/generate-empty-coupons`, {
      generateQty
    });
    return response.data;
  }

  async getDashboardStats(perDate?: string): Promise<DashboardStatsResponse> {
    const params: any = {};
    if (perDate) {
      params.perDate = perDate;
    }
    const response: AxiosResponse<DashboardStatsResponse> = await this.api.get('/admin/dashboard/stats', {
      params
    });
    return response.data;
  }

  async getSubscriptionInvoiceGraph(period: 'weekly' | 'monthly' | 'yearly' = 'monthly'): Promise<SubscriptionInvoiceGraphResponse> {
    const response: AxiosResponse<SubscriptionInvoiceGraphResponse> = await this.api.get('/admin/dashboard/subscription-invoice-graph', {
      params: { period }
    });
    return response.data;
  }

  async getMRRGraph(period: 'weekly' | 'monthly' | 'yearly' = 'monthly'): Promise<MRRGraphResponse> {
    const response: AxiosResponse<MRRGraphResponse> = await this.api.get('/admin/dashboard/mrr-graph', {
      params: { period }
    });
    return response.data;
  }

  async getSalesGraph(period: 'weekly' | 'monthly' | 'yearly' = 'monthly'): Promise<SalesGraphResponse> {
    const response: AxiosResponse<SalesGraphResponse> = await this.api.get('/admin/dashboard/sales-graph', {
      params: { period }
    });
    return response.data;
  }

  async getUsersGraph(period: 'weekly' | 'monthly' | 'yearly' = 'monthly'): Promise<UsersGraphResponse> {
    const response: AxiosResponse<UsersGraphResponse> = await this.api.get('/admin/dashboard/users-graph', {
      params: { period }
    });
    return response.data;
  }

  async getMonthlyReport(period: string): Promise<MonthlyReportResponse> {
    const response: AxiosResponse<MonthlyReportResponse> = await this.api.get('/admin/dashboard/monthly-report', {
      params: { period }
    });
    return response.data;
  }

  async exportMonthlyReport(period: string): Promise<Blob> {
    const response: AxiosResponse<Blob> = await this.api.get('/admin/dashboard/monthly-report/export', {
      params: { period },
      responseType: 'blob'
    });
    return response.data;
  }

  // Affiliate endpoints
  async getAffiliateHistories(
    page: number = 1,
    perPage: number = 15,
    search?: string
  ): Promise<AffiliateHistoriesResponse> {
    const response: AxiosResponse<AffiliateHistoriesResponse> = await this.api.get('/admin/company-affiliate-histories', {
      params: { page, perPage, search }
    });
    return response.data;
  }

  async getCommissionWithdraws(withdrawStatus?: string): Promise<CommissionWithdrawsResponse> {
    const params: any = {};
    if (withdrawStatus) {
      params.withdrawStatus = withdrawStatus;
    }
    
    const response: AxiosResponse<CommissionWithdrawsResponse> = await this.api.get('/admin/company-commission-histories/withdraws', {
      params
    });
    return response.data;
  }

  async registerReferrer(payload: RegisterReferrerPayload): Promise<{ status: string; message: string; data: CompanyAffiliateResource }> {
    const response = await this.api.post('/admin/company-affiliates', payload);
    return response.data;
  }

  async resendReferrerSetPassword(affiliateId: string): Promise<{ status: string; message: string }> {
    const response = await this.api.post(`/admin/company-affiliates/${affiliateId}/resend-set-password`);
    return response.data;
  }

  // URL wa.me dirakit di server: template pesannya ada di env kloa-pos supaya
  // CS bisa memperbaiki kalimatnya tanpa deploy ulang panel ini.
  async getReferrerWhatsappUrl(affiliateId: string): Promise<string> {
    const response = await this.api.get(`/admin/company-affiliates/${affiliateId}/whatsapp-message`);
    return response.data.data.whatsappUrl;
  }

  async getReferrers(page = 1, perPage = 15, search?: string): Promise<{ data: CompanyAffiliateResource[]; meta?: any }> {
    const response = await this.api.get('/admin/company-affiliates', { params: { page, perPage, search } });
    return response.data;
  }

  async rejectReferral(historyId: string, reason: string): Promise<any> {
    const response = await this.api.post(`/admin/company-affiliate-histories/${historyId}/reject`, { reason });
    return response.data;
  }

  async adjustReferralPoints(affiliateId: string, amount: number, reason: string): Promise<any> {
    const response = await this.api.post(`/admin/company-affiliates/${affiliateId}/adjust-points`, { amount, reason });
    return response.data;
  }

  async getAffiliateCommissionRates(affiliateId: string): Promise<{ data: AffiliateCommissionRate[] }> {
    const response = await this.api.get(`/admin/company-affiliates/${affiliateId}/commission-rates`);
    return response.data;
  }

  async setAffiliateCommissionRate(affiliateId: string, payload: SetAffiliateCommissionRatePayload): Promise<any> {
    const response = await this.api.post(`/admin/company-affiliates/${affiliateId}/commission-rates`, payload);
    return response.data;
  }

  async getReferralAuditLog(affiliateId: string): Promise<{ data: ReferralAuditLogEntry[] }> {
    const response = await this.api.get(`/admin/company-affiliates/${affiliateId}/audit-log`);
    return response.data;
  }

  async correctReferralCode(companyId: string, referralCode: string, note: string): Promise<any> {
    const response = await this.api.post(`/admin/company-affiliates/company/${companyId}/correct-code`, { referralCode, note });
    return response.data;
  }

  async markWithdrawSent(commissionId: string, transferProofUrl: string): Promise<any> {
    const response = await this.api.post(`/admin/company-commission-histories/${commissionId}/mark-sent`, { transferProofUrl });
    return response.data;
  }

  async getAffiliateStats(): Promise<AffiliateStatsResponse> {
    const response: AxiosResponse<AffiliateStatsResponse> = await this.api.get('/admin/company-affiliate-histories/stats');
    return response.data;
  }

  async acceptWithdraw(commissionId: string): Promise<any> {
    const response = await this.api.post(`/admin/company-commission-histories/accept-withdraw/${commissionId}`);
    return response.data;
  }

  async rejectWithdraw(commissionId: string): Promise<any> {
    const response = await this.api.post(`/admin/company-commission-histories/reject-withdraw/${commissionId}`);
    return response.data;
  }

  // Company Affiliate Detail endpoints
  async getCompanyAffiliateDetail(companyId: string): Promise<CompanyAffiliateDetailResponse> {
    const response: AxiosResponse<CompanyAffiliateDetailResponse> = await this.api.get(`/admin/company-affiliates/company/${companyId}`);
    return response.data;
  }

  async getCompanyAffiliateStats(companyId: string): Promise<CompanyAffiliateStatsResponse> {
    const response: AxiosResponse<CompanyAffiliateStatsResponse> = await this.api.get(`/admin/company-affiliates/company/${companyId}/stats`);
    return response.data;
  }

  async getCompanyAffiliateHistories(
    companyId: string,
    page: number = 1,
    perPage: number = 15
  ): Promise<CompanyAffiliateHistoriesByCompanyResponse> {
    const response: AxiosResponse<CompanyAffiliateHistoriesByCompanyResponse> = await this.api.get(`/admin/company-affiliate-histories/by-company/${companyId}`, {
      params: { page, perPage }
    });
    return response.data;
  }

  async getCompanyCommissionHistories(
    companyId: string,
    page: number = 1,
    perPage: number = 15
  ): Promise<CompanyCommissionHistoriesByCompanyResponse> {
    const response: AxiosResponse<CompanyCommissionHistoriesByCompanyResponse> = await this.api.get(`/admin/company-commission-histories/by-company/${companyId}`, {
      params: { page, perPage }
    });
    return response.data;
  }

  // Company Follow Up endpoints
  async followUpCompany(companyId: string, followedUpResult: string): Promise<any> {
    const response = await this.api.post(`/admin/companies/${companyId}/follow-up`, {
      followedUpResult
    });
    return response.data;
  }

  async getCompanyFollowUpUrl(companyId: string): Promise<{ followUpUrl: string }> {
    const response: AxiosResponse<{ data: { followUpUrl: string } }> = await this.api.get(`/admin/companies/${companyId}/follow-up-url`);
    return response.data.data;
  }

  // Leads & Sales follow-up endpoints
  async getLeads(params?: { search?: string; perPage?: number; page?: number; businessType?: string; showClosing?: boolean; followUpStatus?: string; lastFollowUpMinDaysAgo?: number }): Promise<PaginatedResponse<Lead>> {
    const { showClosing, ...rest } = params || {};
    // Laravel's `boolean` rule rejects the string "true"/"false" from a query string, so send 1/0.
    const query = { ...rest, ...(showClosing === undefined ? {} : { showClosing: showClosing ? 1 : 0 }) };
    const response: AxiosResponse<PaginatedResponse<Lead>> = await this.api.get('/admin/leads', { params: query });
    return response.data;
  }

  async getReviewFeedbacks(params?: { search?: string; dateFrom?: string; dateTo?: string; perPage?: number; page?: number }): Promise<PaginatedResponse<ReviewFeedbackResource>> {
    const response: AxiosResponse<PaginatedResponse<ReviewFeedbackResource>> = await this.api.get('/admin/review-feedbacks', { params });
    return response.data;
  }

  async extendCompanyTrial(companyId: string, days: 3 | 7): Promise<{ trialEndsAt: string | null }> {
    const response: AxiosResponse<{ data: { trialEndsAt: string | null } }> =
      await this.api.post(`/admin/companies/${companyId}/extend-trial`, { days });
    return response.data.data;
  }

  async getCompanyFollowUps(companyId: string): Promise<{ company: FollowUpCompany; followUps: CompanyFollowUp[] }> {
    const response: AxiosResponse<{ data: { company: FollowUpCompany; followUps: CompanyFollowUp[] } }> = await this.api.get(`/admin/companies/${companyId}/follow-ups`);
    return response.data.data;
  }

  async createCompanyFollowUp(companyId: string, body: { phoneStatus: string; outcomeStatus?: string; notes?: string }, evidence?: File | null): Promise<any> {
    if (evidence) {
      const fd = new FormData();
      fd.append('phoneStatus', body.phoneStatus);
      if (body.outcomeStatus) fd.append('outcomeStatus', body.outcomeStatus);
      if (body.notes) fd.append('notes', body.notes);
      fd.append('evidence', evidence);
      const response = await this.api.post(`/admin/companies/${companyId}/follow-ups`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      return response.data;
    }
    const response = await this.api.post(`/admin/companies/${companyId}/follow-ups`, body);
    return response.data;
  }

  async getSalesDashboard(params?: { period?: string; startDate?: string; endDate?: string }): Promise<SalesDashboard> {
    const response: AxiosResponse<{ status: string; message: string; data: SalesDashboard }> = await this.api.get('/admin/sales/dashboard', { params });
    return response.data.data;
  }

  async getMarketingDashboard(params?: { period?: string; startDate?: string; endDate?: string }): Promise<MarketingDashboard> {
    const response: AxiosResponse<{ data: MarketingDashboard }> = await this.api.get('/admin/marketing/dashboard', { params });
    return response.data.data;
  }

  async setCompanyPhoneValidity(companyId: string, isPhoneNumberValid: boolean): Promise<any> {
    const response = await this.api.post(`/admin/companies/${companyId}/set-phone-validity`, {
      isPhoneNumberValid
    });
    return response.data;
  }

  // Store Items Admin
  async getAdminStoreItems(
    page: number = 1,
    perPage: number = 15,
    search?: string,
    filters?: { category?: 'sales' | 'other'; isActive?: boolean }
  ): Promise<PaginatedResponse<AdminStoreItemResource>> {
    const response: AxiosResponse<PaginatedResponse<AdminStoreItemResource>> =
      await this.api.get('/admin/store-items', {
        params: { page, perPage, search, ...filters },
      });
    return response.data;
  }

  async createStoreItem(data: CreateStoreItemRequest): Promise<AdminStoreItemResource> {
    const response: AxiosResponse<{ data: AdminStoreItemResource }> =
      await this.api.post('/admin/store-items', data);
    return response.data.data;
  }

  async updateStoreItem(id: string, data: Partial<CreateStoreItemRequest>): Promise<AdminStoreItemResource> {
    const response: AxiosResponse<{ data: AdminStoreItemResource }> =
      await this.api.put(`/admin/store-items/${id}`, data);
    return response.data.data;
  }

  async deleteStoreItem(id: string): Promise<void> {
    await this.api.delete(`/admin/store-items/${id}`);
  }

  // Store Invoices Admin
  async getAdminStoreInvoices(
    page: number = 1,
    perPage: number = 15,
    search?: string,
    filters?: { status?: 'unpaid' | 'paid' | 'expired'; storeItemId?: string; companyId?: string }
  ): Promise<PaginatedResponse<AdminStoreInvoiceResource>> {
    const response: AxiosResponse<PaginatedResponse<AdminStoreInvoiceResource>> =
      await this.api.get('/admin/store-invoices', {
        params: { page, perPage, search, ...filters },
      });
    return response.data;
  }

  async getAdminStoreInvoice(id: string): Promise<AdminStoreInvoiceResource> {
    const response: AxiosResponse<{ data: AdminStoreInvoiceResource }> =
      await this.api.get(`/admin/store-invoices/${id}`);
    return response.data.data;
  }

  async markStoreInvoicePaidManual(id: string, data: MarkStoreInvoicePaidRequest): Promise<{ status: string; message: string }> {
    const response = await this.api.post(`/admin/store-invoices/${id}/paid-manual`, data);
    return response.data;
  }

  // Subscription Voucher endpoints
  async getSubscriptionVouchers(page: number = 1, perPage: number = 10, search?: string, status?: string): Promise<PaginatedResponse<SubscriptionVoucherResource>> {
    const params: any = { page, perPage };
    if (search) params.search = search;
    if (status) params.status = status;
    const response: AxiosResponse<PaginatedResponse<SubscriptionVoucherResource>> = await this.api.get('/admin/subscription-vouchers', { params });
    return response.data;
  }

  async generateSubscriptionVoucher(data: GenerateVoucherRequest): Promise<SubscriptionVoucherResource> {
    const response: AxiosResponse<{ data: SubscriptionVoucherResource }> = await this.api.post('/admin/subscription-vouchers', data);
    return response.data.data;
  }

  async revokeSubscriptionVoucher(id: string): Promise<void> {
    await this.api.delete(`/admin/subscription-vouchers/${id}`);
  }

  async resendSubscriptionVoucher(id: string): Promise<void> {
    await this.api.post(`/admin/subscription-vouchers/${id}/resend`);
  }

  // Admin Users (Kelola Admin)
  async getAdminUsers(): Promise<AdminUser[]> {
    const response: AxiosResponse<{ data: AdminUser[] }> = await this.api.get('/admin/admin-users');
    return response.data.data;
  }

  async createAdminUser(data: { fullName: string; email: string; password: string; adminRole: 'full' | 'readonly' | 'sales' | 'marketing' }): Promise<any> {
    const response = await this.api.post('/admin/admin-users', data);
    return response.data;
  }

  async updateAdminUser(id: string, data: { fullName?: string; adminRole?: 'full' | 'readonly' | 'sales' | 'marketing'; password?: string }): Promise<any> {
    const response = await this.api.put(`/admin/admin-users/${id}`, data);
    return response.data;
  }

  async deleteAdminUser(id: string): Promise<any> {
    const response = await this.api.delete(`/admin/admin-users/${id}`);
    return response.data;
  }

  // Bundling subscription + store item
  async getBundles(
    page: number = 1,
    perPage: number = 15,
    filters?: { search?: string; isActive?: boolean; availableOnly?: boolean }
  ): Promise<PaginatedResponse<SubscriptionBundleResource>> {
    const params: any = { page, perPage };
    if (filters?.search) params.search = filters.search;
    if (filters?.isActive !== undefined) params.isActive = filters.isActive ? 1 : 0;
    if (filters?.availableOnly) params.availableOnly = 1;
    const response: AxiosResponse<PaginatedResponse<SubscriptionBundleResource>> = await this.api.get('/admin/bundles', { params });
    return response.data;
  }

  async createBundle(data: SubscriptionBundleRequest): Promise<SubscriptionBundleResource> {
    const response: AxiosResponse<{ data: SubscriptionBundleResource }> = await this.api.post('/admin/bundles', data);
    return response.data.data;
  }

  /** Harga bundling dari isian form yang belum disimpan (dihitung backend, satu sumber rumus). */
  async previewBundle(data: Omit<SubscriptionBundleRequest, 'name' | 'isActive'> & { name?: string }): Promise<{
    lines: Array<{ itemType: string; name: string; months: number | null; normalPrice: number }>;
    subtotal: number;
    discount: number;
    total: number;
  }> {
    const response = await this.api.post('/admin/bundles/preview', data);
    return response.data.data;
  }

  async updateBundle(id: string, data: SubscriptionBundleRequest): Promise<SubscriptionBundleResource> {
    const response: AxiosResponse<{ data: SubscriptionBundleResource }> = await this.api.put(`/admin/bundles/${id}`, data);
    return response.data.data;
  }

  async toggleBundle(id: string, isActive: boolean): Promise<SubscriptionBundleResource> {
    const response: AxiosResponse<{ data: SubscriptionBundleResource }> = await this.api.patch(`/admin/bundles/${id}/toggle`, { isActive });
    return response.data.data;
  }

  // Pembelian manual (pameran / web)
  async getManualPurchases(
    page: number = 1,
    perPage: number = 15,
    filters?: { search?: string; status?: ManualPurchaseStatus }
  ): Promise<PaginatedResponse<ManualPurchaseResource>> {
    const params: any = { page, perPage };
    if (filters?.search) params.search = filters.search;
    if (filters?.status) params.status = filters.status;
    const response: AxiosResponse<PaginatedResponse<ManualPurchaseResource>> = await this.api.get('/admin/manual-purchases', { params });
    return response.data;
  }

  async getManualPurchase(id: string): Promise<ManualPurchaseResource> {
    const response: AxiosResponse<{ data: ManualPurchaseResource }> = await this.api.get(`/admin/manual-purchases/${id}`);
    return response.data.data;
  }

  async previewManualPurchase(data: { bundleId?: string | null; extraItems: ManualPurchaseExtraItem[]; email?: string }): Promise<ManualPurchasePreview> {
    const response: AxiosResponse<{ data: ManualPurchasePreview }> = await this.api.post('/admin/manual-purchases/preview', data);
    return response.data.data;
  }

  async createManualPurchase(data: {
    bundleId?: string | null;
    extraItems: ManualPurchaseExtraItem[];
    email: string;
    notes?: string;
    idempotencyKey: string;
    paymentProof: File;
  }): Promise<{ data: ManualPurchaseResource; message: string }> {
    const fd = new FormData();
    if (data.bundleId) fd.append('bundleId', data.bundleId);
    fd.append('extraItems', JSON.stringify(data.extraItems));
    fd.append('email', data.email);
    if (data.notes) fd.append('notes', data.notes);
    fd.append('idempotencyKey', data.idempotencyKey);
    fd.append('paymentProof', data.paymentProof);
    const response = await this.api.post('/admin/manual-purchases', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    return response.data;
  }

  /** Bukti bayar ada di storage private: diambil sebagai blob karena <img src> tidak bisa mengirim Bearer token. */
  async getManualPurchaseProof(id: string): Promise<Blob> {
    const response: AxiosResponse<Blob> = await this.api.get(`/admin/manual-purchases/${id}/proof`, { responseType: 'blob' });
    return response.data;
  }

  async cancelManualPurchase(id: string): Promise<ManualPurchaseResource> {
    const response: AxiosResponse<{ data: ManualPurchaseResource }> = await this.api.post(`/admin/manual-purchases/${id}/cancel`);
    return response.data.data;
  }

  async changeManualPurchaseEmail(id: string, email: string): Promise<{ data: ManualPurchaseResource; message: string }> {
    const response = await this.api.patch(`/admin/manual-purchases/${id}/email`, { email });
    return response.data;
  }

  async retryManualPurchase(id: string): Promise<ManualPurchaseResource> {
    const response: AxiosResponse<{ data: ManualPurchaseResource }> = await this.api.post(`/admin/manual-purchases/${id}/retry`);
    return response.data.data;
  }

  async resendManualPurchaseEmail(id: string): Promise<void> {
    await this.api.post(`/admin/manual-purchases/${id}/resend-email`);
  }

  // Pendaftaran QRIS dinamis (Paydia)
  async getQrisAccounts(params: {
    page: number;
    perPage: number;
    status?: QrisAccountStatus;
    type?: QrisAccountType;
    search?: string;
  }): Promise<PaginatedResponse<QrisAccountListItem>> {
    const query: any = { page: params.page, perPage: params.perPage };
    if (params.status) query.status = params.status;
    if (params.type) query.type = params.type;
    if (params.search) query.search = params.search;
    const response: AxiosResponse<PaginatedResponse<QrisAccountListItem>> = await this.api.get('/admin/qris-accounts', { params: query });
    return response.data;
  }

  async getQrisAccount(id: string): Promise<QrisAccountDetail> {
    const response: AxiosResponse<{ data: QrisAccountDetail }> = await this.api.get(`/admin/qris-accounts/${id}`);
    return response.data.data;
  }

  /** Berisi kata sandi & PIN: hanya admin full (403 untuk readonly). */
  async exportQrisAccounts(type: QrisAccountType, ids?: string[]): Promise<Blob> {
    const params: any = { type };
    if (ids && ids.length) params.ids = ids;
    const response: AxiosResponse<Blob> = await this.api.get('/admin/qris-accounts/export', { params, responseType: 'blob' });
    return response.data;
  }

  async downloadQrisDocument(id: string, docId: string): Promise<Blob> {
    const response: AxiosResponse<Blob> = await this.api.get(`/admin/qris-accounts/${id}/documents/${docId}`, { responseType: 'blob' });
    return response.data;
  }

  async downloadQrisDocumentsZip(id: string): Promise<Blob> {
    const response: AxiosResponse<Blob> = await this.api.get(`/admin/qris-accounts/${id}/documents.zip`, { responseType: 'blob' });
    return response.data;
  }

  async markQrisUploaded(id: string): Promise<{ status: string; message: string }> {
    const response = await this.api.post(`/admin/qris-accounts/${id}/mark-uploaded`);
    return response.data;
  }

  async activateQrisAccount(
    id: string,
    data: { mid: string; merchantPan?: string; clientId: string; clientSecret: string; merchantName?: string },
  ): Promise<{ status: string; message: string }> {
    const response = await this.api.post(`/admin/qris-accounts/${id}/activate`, data);
    return response.data;
  }

  async changeQrisPaydiaClient(
    id: string,
    data: { mid?: string; clientId: string; clientSecret: string },
  ): Promise<{ status: string; message: string }> {
    const response = await this.api.post(`/admin/qris-accounts/${id}/paydia-client`, data);
    return response.data;
  }

  async rejectQrisAccount(id: string, data: { reason: string }): Promise<{ status: string; message: string }> {
    const response = await this.api.post(`/admin/qris-accounts/${id}/reject`, data);
    return response.data;
  }

  async refundQrisAccount(id: string, data: { note: string }): Promise<{ status: string; message: string }> {
    const response = await this.api.post(`/admin/qris-accounts/${id}/refund`, data);
    return response.data;
  }

  // Riwayat Import Foto Menu (AI)
  /** Daftar berhalaman: bentuk {data, links, meta} langsung di response.data (tanpa envelope status). */
  async getAdminMenuImports(
    page: number = 1,
    perPage: number = 15,
    filters: AdminMenuImportFilters = {}
  ): Promise<PaginatedResponse<AdminMenuImportRow>> {
    const response: AxiosResponse<PaginatedResponse<AdminMenuImportRow>> = await this.api.get('/admin/menu-imports', {
      params: { page, perPage, ...this.cleanParams(filters) },
    });
    return response.data;
  }

  /** Ringkasan memakai envelope {status, message?, data} → response.data.data. */
  async getAdminMenuImportSummary(filters: AdminMenuImportFilters = {}): Promise<AdminMenuImportSummary> {
    const response: AxiosResponse<{ status: string; data: AdminMenuImportSummary }> = await this.api.get('/admin/menu-imports/summary', {
      params: this.cleanParams(filters),
    });
    return response.data.data;
  }

  async getAdminMenuImport(id: string): Promise<AdminMenuImportDetail> {
    const response: AxiosResponse<{ status: string; data: AdminMenuImportDetail }> = await this.api.get(`/admin/menu-imports/${id}`);
    return response.data.data;
  }

  async getAdminMenuImportSettings(): Promise<MenuImportSettings> {
    const response: AxiosResponse<{ status: string; data: MenuImportSettings }> = await this.api.get('/admin/menu-import-settings');
    return response.data.data;
  }

  async updateAdminMenuImportSettings(body: { model: string; effort: string }): Promise<MenuImportSettings> {
    const response: AxiosResponse<{ status: string; data: MenuImportSettings }> = await this.api.put('/admin/menu-import-settings', body);
    return response.data.data;
  }

  async exportAdminMenuImports(filters: AdminMenuImportFilters = {}): Promise<Blob> {
    const response: AxiosResponse<Blob> = await this.api.get('/admin/menu-imports/export', {
      params: this.cleanParams(filters),
      responseType: 'blob',
    });
    return response.data;
  }

  private cleanParams(filters: AdminMenuImportFilters): Record<string, string> {
    const params: Record<string, string> = {};
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '' && !(key === 'result' && value === 'all')) {
        params[key] = String(value);
      }
    });
    return params;
  }
}

export default new ApiService();
